import { FileText, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { CtaLink } from "../CtaLink";
import { Reveal } from "../motion/Reveal";
import {
  annualTotal,
  capacityLabel,
  lateNoticeLabel,
  TIERS,
  type BillingCycle,
  type Tier,
} from "./pricing-tiers";

export { TIERS, ENTERPRISE, INCLUDED } from "./pricing-tiers";
export type { BillingCycle, Tier } from "./pricing-tiers";

const euros = (n: number) => `${n.toLocaleString("fr-FR").replace(/\s/g, " ")} €`;

export function PricingCards({
  cycle,
  tiers = TIERS,
}: {
  cycle: BillingCycle;
  tiers?: Tier[];
}) {
  return (
    <div
      className={cn(
        "grid gap-5",
        tiers.length === 3 ? "md:grid-cols-3" : "md:grid-cols-2 xl:grid-cols-4",
      )}
    >
      {tiers.map((tier, i) => (
        <Reveal key={tier.id} delay={i * 80} className="h-full">
          <TierCard tier={tier} cycle={cycle} />
        </Reveal>
      ))}
    </div>
  );
}

function Price({ tier, cycle }: { tier: Tier; cycle: BillingCycle }) {
  if (tier.price === "custom") {
    return (
      <div>
        <p className="font-display text-4xl font-semibold tracking-tight text-foreground">Sur devis</p>
        <p className="mt-1 text-caption text-muted-foreground">selon votre volume</p>
      </div>
    );
  }
  const amount = cycle === "annual" ? tier.price.annual : tier.price.monthly;
  const total = annualTotal(tier);
  const detail =
    amount === 0
      ? "sans limite de durée"
      : cycle === "annual"
        ? `par mois, soit ${euros(total ?? 0)} par an`
        : "par mois";
  return (
    <div>
      <p
        key={`${tier.id}-${cycle}`}
        className="font-display text-5xl font-semibold tracking-tight text-foreground tabular-nums"
        style={{ animation: "enc-rise 320ms var(--ease-quint-out) both" }}
      >
        {euros(amount)}
      </p>
      <p className="mt-1 text-caption text-muted-foreground">{detail}</p>
    </div>
  );
}

export function TierCard({ tier, cycle }: { tier: Tier; cycle: BillingCycle }) {
  return (
    <article
      id={`offre-${tier.id}`}
      className={cn(
        "relative flex h-full flex-col rounded-panel border bg-surface p-6",
        tier.featured
          ? "border-action shadow-pop ring-1 ring-action"
          : "border-border shadow-card",
      )}
    >
      {tier.featured && (
        <span className="absolute -top-3 left-6 rounded-full bg-action px-3 py-1 text-2xs font-semibold tracking-[0.12em] text-action-foreground uppercase">
          Pour une petite équipe
        </span>
      )}
      <h3 className="font-display text-xl font-semibold text-foreground">{tier.name}</h3>
      <p className="mt-1 min-h-10 text-sm text-muted-foreground">{tier.description}</p>
      <div className="mt-5">
        <Price tier={tier} cycle={cycle} />
      </div>
      <ul className="mt-5 flex-1 space-y-2 border-t border-border pt-5 text-sm text-foreground">
        <li className="flex gap-2.5">
          <Users aria-hidden className="mt-0.5 size-4 shrink-0 text-action" strokeWidth={1.75} />
          {capacityLabel(tier)}
        </li>
        <li className="flex gap-2.5">
          <FileText aria-hidden className="mt-0.5 size-4 shrink-0 text-action" strokeWidth={1.75} />
          {lateNoticeLabel(tier)}
        </li>
      </ul>
      <CtaLink
        to={tier.cta.to}
        variant={tier.featured ? "primary" : "secondary"}
        className="mt-6 w-full justify-center"
      >
        {tier.cta.label}
      </CtaLink>
    </article>
  );
}

/** L'offre sur devis, en largeur, sous les quatre autres. */
export function WideTierCard({ tier }: { tier: Tier }) {
  return (
    <article id={`offre-${tier.id}`} className="grid gap-6 rounded-panel border border-border bg-surface p-6 shadow-card md:grid-cols-[1.1fr_1fr_auto] md:items-center">
      <div>
        <h3 className="font-display text-xl font-semibold text-foreground">{tier.name}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{tier.description}</p>
      </div>
      <ul className="space-y-2 text-sm text-foreground">
        <li className="flex gap-2.5">
          <Users aria-hidden className="mt-0.5 size-4 shrink-0 text-action" strokeWidth={1.75} />
          {capacityLabel(tier)}
        </li>
        <li className="flex gap-2.5">
          <FileText aria-hidden className="mt-0.5 size-4 shrink-0 text-action" strokeWidth={1.75} />
          {lateNoticeLabel(tier)}
        </li>
      </ul>
      <div className="flex flex-col items-start gap-3 md:items-end">
        <p className="font-display text-2xl font-semibold text-foreground">Sur devis</p>
        <CtaLink to={tier.cta.to} variant="secondary" arrow>
          {tier.cta.label}
        </CtaLink>
      </div>
    </article>
  );
}

const CYCLES: Array<{ value: BillingCycle; label: string; hint?: string }> = [
  { value: "monthly", label: "Mensuel" },
  { value: "annual", label: "Annuel", hint: "≈ −20 %" },
];

/** Choix immédiat, texte et fond changent ensemble pour conserver leur contraste. */
export function BillingToggle({
  cycle,
  onChange,
}: {
  cycle: BillingCycle;
  onChange: (c: BillingCycle) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Rythme de facturation"
      className="inline-flex items-center rounded-full border border-border bg-surface p-1 shadow-card"
    >
      {CYCLES.map((c) => {
        const active = cycle === c.value;
        return (
          <button
            key={c.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(c.value)}
            className={cn(
              "relative inline-flex h-11 cursor-pointer items-center gap-2 rounded-full px-5 text-ui font-semibold transition-colors duration-150",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span className="relative">{c.label}</span>
            {c.hint && (
              <span
                className={cn(
                  "relative rounded-full px-1.5 py-0.5 text-2xs font-bold",
                  active ? "bg-action-on-primary text-primary" : "bg-action-soft text-action",
                )}
              >
                {c.hint}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
